import * as React from 'react';
import { useEffect, useState } from 'react';

import { useDispatch } from 'react-redux';
import { AnyAction } from '@reduxjs/toolkit';
import { ThunkDispatch } from '@reduxjs/toolkit';

import { api } from '@api/reduxApi';
import { SurveyBuilder } from '@components/SurveyBuilder';
import * as Models from '@components/SurveyBuilder/types/models';

import { AudienceCard } from './components/AudienceCard';
import { Participants } from './components/Participants';
import { StudyDetails } from './components/StudyDetails';
import { Generative } from './Generative';
import { Tabs, TabType } from './Tabs';
import { MainAgentState } from './types';

export const Display = ({
  state,
  setState,
  isPublished,
  isLoading,
  nodeName
}: {
  state: MainAgentState;
  setState: (state: MainAgentState) => void;
  isPublished: boolean;
  isLoading?: boolean;
  nodeName?: string;
}): React.ReactElement => {
  const { study, recruitmentRequest, questions } = state;
  const [updateStudy] = api.useUpdateStudyMutation();
  const [currentTab, setCurrentTab] = useState<TabType>('overview');
  const [activeBlock, setActiveBlock] = useState<Models.Block>();
  const dispatch = useDispatch<ThunkDispatch<unknown, unknown, AnyAction>>();

  const hasQuestions = questions && questions.length > 0;

  const handleStudyFieldChange = (field: string, value: string) => {
    const typedStudy = study as any;
    // Convert string values to numbers for numeric fields
    const processedValue =
      typeof value === 'string' && (field === 'incentive' || field === 'participant_limit') ? Number(value) : value;

    const updatedStudy = { ...typedStudy, [field]: processedValue };
    setState({ ...state, study: updatedStudy });
    updateStudy({ id: study.id, [field]: processedValue } as any);
  };

  useEffect(() => {
    if (isPublished) setCurrentTab('participants');
  }, [isPublished]);

  useEffect(() => {
    dispatch(api.util.invalidateTags(['SurveyBuilderBlocks']));
  }, [JSON.stringify({ questions })]);

  if (!study) {
    return <></>;
  }

  return (
    <div className='flex h-full'>
      <div className='border-l border-r border-gray-200'>
        <Tabs current={currentTab} onSelect={setCurrentTab} isPublished={isPublished} />
      </div>
      <div className='relative flex-1 overflow-y-auto p-6'>
        {isLoading && (
          <>
            <div className='absolute inset-0 bg-white' style={{ opacity: 0.8, zIndex: 100 }} />
            <div className='absolute inset-0' style={{ zIndex: 101 }}>
              <Generative state={state} />
            </div>
          </>
        )}
        {currentTab === 'overview' && (
          <div className='flex flex-col space-y-6'>
            <StudyDetails method={state.method} study={study} onFieldChange={handleStudyFieldChange} />
            {hasQuestions && <SurveyBuilder isStudioVersion surveyId={study.id} setActiveBlock={setActiveBlock} />}
          </div>
        )}
        {currentTab === 'plan' && (
          <StudyDetails method={state.method} study={study} onFieldChange={handleStudyFieldChange} />
        )}
        {currentTab === 'audience' && study && recruitmentRequest && (
          <AudienceCard study={study} request={recruitmentRequest} />
        )}
        {currentTab === 'test' && hasQuestions && (
          <div className='flex flex-col space-y-6'>
            <SurveyBuilder isStudioVersion surveyId={study.id} setActiveBlock={setActiveBlock} />
          </div>
        )}
        {currentTab === 'participants' && isPublished && <Participants />}
      </div>
    </div>
  );
};
